<template>
  <div class="overflow-x-auto">
    <!-- 加载中状态 -->
    <div v-if="loading" class="flex justify-center items-center py-10">
      <div class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      <span>加载中...</span>
    </div>

    <!-- 无数据状态 -->
    <div v-else-if="!items.length" class="text-center py-4 text-gray-500">
      暂无数据
    </div>

    <!-- 表格数据 -->
    <table v-else class="min-w-full divide-y divide-gray-200">
      <thead class="bg-gray-50">
        <tr>
          <th 
            v-for="column in columns" 
            :key="column.key" 
            scope="col" 
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            :class="[column.headerClass, column.width ? column.width : '']"
          >
            <div class="flex items-center cursor-pointer" @click="column.sortable !== false && handleSort(column.key)">
              {{ column.label }}
              <span v-if="column.sortable !== false" class="ml-1 text-gray-400">
                <i v-if="sortKey === column.key" :class="['fas', sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down']"></i>
                <i v-else class="fas fa-sort text-gray-300"></i>
              </span>
            </div>
          </th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200">
        <tr v-for="(item, index) in items" :key="item.id || index" :class="rowClass(item, index)">
          <td 
            v-for="column in columns" 
            :key="column.key" 
            class="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
            :class="getCellClass(column, item)"
          >
            <!-- 使用具名插槽渲染自定义内容 -->
            <slot :name="`cell-${column.key}`" :item="item" :index="index">
              <!-- 默认渲染方式 -->
              <template v-if="column.formatter">
                {{ column.formatter(item[column.key], item) }}
              </template>
              <template v-else>
                {{ item[column.key] }}
              </template>
            </slot>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 分页控件 -->
    <div v-if="showPagination && items.length > 0" class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      <div class="flex-1 flex justify-between sm:hidden">
        <button 
          @click="handlePageChange(currentPage - 1)" 
          :disabled="currentPage <= 1"
          :class="[
            'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50',
            currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
          ]"
        >
          上一页
        </button>
        <button 
          @click="handlePageChange(currentPage + 1)" 
          :disabled="currentPage >= totalPages"
          :class="[
            'ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50',
            currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
          ]"
        >
          下一页
        </button>
      </div>
      <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p class="text-sm text-gray-700">
            显示第
            <span class="font-medium">{{ startItem }}</span>
            到
            <span class="font-medium">{{ endItem }}</span>
            条，共
            <span class="font-medium">{{ totalItems }}</span>
            条结果
          </p>
        </div>
        <div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              @click="handlePageChange(currentPage - 1)"
              :disabled="currentPage <= 1"
              class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              :class="currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
            >
              <span class="sr-only">上一页</span>
              <i class="fas fa-chevron-left"></i>
            </button>
            
            <!-- 页码按钮 -->
            <template v-for="page in displayedPages" :key="page">
              <button
                @click="handlePageChange(page)"
                :class="[
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                  currentPage === page 
                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' 
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                ]"
              >
                {{ page }}
              </button>
            </template>
            
            <button
              @click="handlePageChange(currentPage + 1)"
              :disabled="currentPage >= totalPages"
              class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              :class="currentPage >= totalPages ? 'opacity-50 cursor-not-allowed' : ''"
            >
              <span class="sr-only">下一页</span>
              <i class="fas fa-chevron-right"></i>
            </button>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  // 表格列配置
  columns: {
    type: Array,
    required: true
  },
  // 表格数据
  items: {
    type: Array,
    default: () => []
  },
  // 是否加载中
  loading: {
    type: Boolean,
    default: false
  },
  // 空数据文案
  emptyText: {
    type: String,
    default: '暂无相关数据'
  },
  // 分页相关
  showPagination: {
    type: Boolean,
    default: true
  },
  currentPage: {
    type: Number,
    default: 1
  },
  itemsPerPage: {
    type: Number,
    default: 10
  },
  totalItems: {
    type: Number,
    default: 0
  },
  // 排序相关
  initialSortKey: {
    type: String,
    default: ''
  },
  initialSortOrder: {
    type: String,
    default: 'asc'
  },
  // 行样式
  rowClassName: {
    type: [String, Function],
    default: ''
  }
});

const emit = defineEmits(['page-change', 'sort', 'row-click']);

// 排序状态
const sortKey = ref(props.initialSortKey);
const sortOrder = ref(props.initialSortOrder);

// 监听props变化
watch(() => props.initialSortKey, (val) => {
  sortKey.value = val;
});

watch(() => props.initialSortOrder, (val) => {
  sortOrder.value = val;
});

// 计算属性
const totalPages = computed(() => {
  return Math.ceil(props.totalItems / props.itemsPerPage);
});

const startItem = computed(() => {
  if (props.totalItems === 0) return 0;
  return (props.currentPage - 1) * props.itemsPerPage + 1;
});

const endItem = computed(() => {
  const end = props.currentPage * props.itemsPerPage;
  return end > props.totalItems ? props.totalItems : end;
});

const displayedPages = computed(() => {
  const maxVisiblePages = 5;
  const totalPagesValue = totalPages.value;
  const currentPageValue = props.currentPage;
  
  if (totalPagesValue <= maxVisiblePages) {
    return Array.from({ length: totalPagesValue }, (_, i) => i + 1);
  }
  
  // 显示当前页附近的页码
  let startPage = Math.max(1, currentPageValue - Math.floor(maxVisiblePages / 2));
  let endPage = startPage + maxVisiblePages - 1;
  
  if (endPage > totalPagesValue) {
    endPage = totalPagesValue;
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }
  
  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
});

// 方法
function handlePageChange(page) {
  if (page < 1 || page > totalPages.value || props.loading) {
    return;
  }
  
  emit('page-change', page);
}

function handleSort(key) {
  if (sortKey.value === key) {
    // 切换排序方向
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    // 设置新的排序字段
    sortKey.value = key;
    sortOrder.value = 'asc';
  }
  
  emit('sort', { key, direction: sortOrder.value });
}

function rowClass(item, index) {
  if (typeof props.rowClassName === 'function') {
    return props.rowClassName(item, index);
  }
  return props.rowClassName;
}

function getCellClass(column, item) {
  let classes = column.cellClass || '';
  
  if (typeof column.cellClass === 'function') {
    classes = column.cellClass(item[column.key], item);
  }
  
  return classes;
}

// 暴露方法
defineExpose({
  sortKey,
  sortOrder,
  resetSort() {
    sortKey.value = props.initialSortKey;
    sortOrder.value = props.initialSortOrder;
  }
});
</script> 